import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { uniqBy } from "lodash";

import { st, OrderFullDecorator, Accordion, Item, Grid, MainView, Form, FormControl, Button } from "lib/index";
import NewOrderModalView from "../NewOrderModalView";
import { stateSelector, fetchProducts, setFilters } from "./index";

@connect(
  (state) => ({
    currentCompany: state.auth.company.model,
    filteredItems: stateSelector(state).filteredItems,
    filters: stateSelector(state).filters,
    fetchDone: stateSelector(state).products.fetchDone,
    fetchLoading: stateSelector(state).products.fetchLoading,
    fetchFailed: stateSelector(state).products.fetchFailed,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        fetchProducts,
        setFilters,
      },
      dispatch
    )
)
export default class NewOrderProductModal extends React.PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      useCustomProduct: false,
      activeIndex: null,
    };
  }

  componentDidMount() {
    const { order, setFilters, fetchProducts } = this.props;
    if (order.product) {
      setFilters({ productId: order.product.id });
    } else if (order.product_name) {
      this.setState({ useCustomProduct: true });
    }
    fetchProducts();
  }

  handleProductSelect(product, product_variant) {
    const { currentCompany, onOrderUpdate, onSave } = this.props;
    const order = OrderFullDecorator.build({
      company: product.company,
      product,
      product_variant,
      agent: currentCompany.id === product.company.id ? null : currentCompany,
    });
    onOrderUpdate(order);
    onSave();
  }

  handleOpenAccordion = (e, { index }) => {
    const { activeIndex } = this.state;
    const newIndex = activeIndex === index ? -1 : index;

    this.setState({ activeIndex: newIndex });
  };

  handleSubmit(event) {
    const { onSave } = this.props;
    event.preventDefault();
    onSave();
  }

  handleOrderPropChange(prop, value) {
    const { order, onOrderUpdate } = this.props;
    onOrderUpdate(
      new OrderFullDecorator({
        ...order,
        [prop]: value,
        ...(prop === "booking_type"
          ? {
              product_schedule_slot: null,
              stock_useby_starts_at: null,
              stock_useby_ends_at: null,
            }
          : {}),
      })
    );
  }

  renderItemsList(items) {
    const { filters } = this.props;
    return (
      <div className="ui items items-halfs">
        {items
          .filter(({ product }) => product.product_type !== "accommodation")
          .map(({ product, product_variant }) => {
            let productImageUrl = st.getAttachmentImageUrl(product, "main_image", "thumb");
            if (productImageUrl.endsWith(".webp")) {
              productImageUrl = product.main_image_file.url;
            }

            return (
              <a
                key={`${product.uuid}-${product_variant.uuid}`}
                className="item"
                onClick={this.handleProductSelect.bind(this, product, product_variant)}
              >
                <Item.Image size="small" src={productImageUrl} />
                <Item.Content verticalAlign="middle">
                  <Item.Header>{filters.productId ? product_variant.name : product.name}</Item.Header>
                  <Item.Meta>{filters.productId ? product.name : product_variant.name}</Item.Meta>
                </Item.Content>
              </a>
            );
          })}
      </div>
    );
  }

  renderProductsListContent() {
    const { currentCompany, fetchLoading, fetchFailed, fetchDone, fetchProducts, filteredItems } = this.props;
    const { activeIndex } = this.state;
    const companies = uniqBy(
      filteredItems.map(({ product: p }) => p.company),
      (c) => c.id
    );
    return (
      <NewOrderModalView.Content>
        <MainView.Dimmer loading={!fetchDone && fetchLoading} failed={fetchFailed} retry={fetchProducts} />
        {fetchDone &&
          (filteredItems.length > 0 ? (
            <div>
              {currentCompany.agent && (
                <Accordion fluid styled exclusive={false} defaultActiveIndex={companies.map((c, i) => i)}>
                  {companies.map((company) => [
                    <Accordion.Title
                      key={`title-${company.uuid}`}
                      active={activeIndex === company.uuid}
                      index={company.uuid}
                      onClick={this.handleOpenAccordion}
                    >
                      {company.name} <i className="icon dropdown" />
                    </Accordion.Title>,
                    <Accordion.Content key={`content-${company.uuid}`} active={activeIndex === company.uuid}>
                      <div className="ui items items-halfs">
                        {this.renderItemsList(filteredItems.filter(({ product: p }) => p.company.id === company.id))}
                      </div>
                    </Accordion.Content>,
                  ])}
                </Accordion>
              )}

              {!currentCompany.agent && this.renderItemsList(filteredItems)}
            </div>
          ) : (
            <div>
              <p>{I18n.t("papp.orders.NewOrderProductModal.no_products_found")}</p>
            </div>
          ))}
      </NewOrderModalView.Content>
    );
  }

  renderCustomProductFormContent() {
    const { currentCompany, order, loading, errors, valid } = this.props;
    return (
      <NewOrderModalView.Content>
        <Form onSubmit={this.handleSubmit.bind(this)} error={!valid}>
          {!!errors.base && <FormMessage error={errors.base} />}

          <FormControl.InputFormGroup
            {...FormControl.addProps(`order.product_name`, { errors })}
            value={order.product_name}
            onChange={this.handleOrderPropChange.bind(this, "product_name")}
            label={I18n.humanAttributeName("order", "product_name")}
            required
            autoFocus
          />

          <FormControl.CheckboxFormGroup
            {...FormControl.addProps(`order.booking_type`, { errors })}
            checked={order.booking_type === "static_date_booking"}
            onChange={(checked) =>
              this.handleOrderPropChange("booking_type", checked ? "static_date_booking" : "no_booking")
            }
            label={I18n.t("papp.orders.NewOrderProductModal.static_date_booking_label")}
            toggle
          />

          {order.booking_type === "no_booking" && (
            <Grid columns={2}>
              <Grid.Column>
                <FormControl.DateTimeFormGroup
                  {...FormControl.addProps(`order.stock_useby_starts_at`, {
                    errors,
                  })}
                  value={order.stock_useby_starts_at}
                  onChange={this.handleOrderPropChange.bind(this, "stock_useby_starts_at")}
                  label={I18n.humanAttributeName("order", "stock_useby_starts_at")}
                  timeZoneName={order.company_time_zone_name}
                />
              </Grid.Column>

              <Grid.Column>
                <FormControl.DateTimeFormGroup
                  {...FormControl.addProps(`order.stock_useby_ends_at`, {
                    errors,
                  })}
                  value={order.stock_useby_ends_at}
                  onChange={this.handleOrderPropChange.bind(this, "stock_useby_ends_at")}
                  label={I18n.humanAttributeName("order", "stock_useby_ends_at")}
                  timeZoneName={order.company_time_zone_name}
                />
              </Grid.Column>
            </Grid>
          )}

          <Grid columns={2}>
            <Grid.Column>
              <FormControl.MultipleInputFormGroup
                {...FormControl.addProps(`order.product_included_items`, {
                  errors,
                })}
                value={order.product_included_items}
                onChange={this.handleOrderPropChange.bind(this, "product_included_items")}
                label={I18n.humanAttributeName("product", "included_items")}
                placeholder={I18n.humanAttributeName("product", "included_items_placeholder")}
              />
            </Grid.Column>

            <Grid.Column>
              <FormControl.MultipleInputFormGroup
                {...FormControl.addProps(`order.product_excluded_items`, {
                  errors,
                })}
                value={order.product_excluded_items}
                onChange={this.handleOrderPropChange.bind(this, "product_excluded_items")}
                label={I18n.humanAttributeName("product", "excluded_items")}
                placeholder={I18n.humanAttributeName("product", "excluded_items_placeholder")}
              />
            </Grid.Column>
          </Grid>

          <Grid columns={2} className="u-marginBottom">
            <Grid.Column>
              <FormControl.MultipleInputFormGroup
                {...FormControl.addProps(`order.product_customer_requirements`, { errors })}
                value={order.product_customer_requirements}
                onChange={this.handleOrderPropChange.bind(this, "product_customer_requirements")}
                label={I18n.humanAttributeName("product", "customer_requirements")}
                placeholder={I18n.humanAttributeName("product", "customer_requirements_placeholder")}
              />
            </Grid.Column>

            <Grid.Column>
              <FormControl.MultipleInputFormGroup
                {...FormControl.addProps(`order.product_customer_hints`, {
                  errors,
                })}
                value={order.product_customer_hints}
                onChange={this.handleOrderPropChange.bind(this, "product_customer_hints")}
                label={I18n.humanAttributeName("product", "customer_hints")}
                placeholder={I18n.humanAttributeName("product", "customer_hints_placeholder")}
              />
            </Grid.Column>
          </Grid>

          <FormControl.TextAreaFormGroup
            {...FormControl.addProps(`order.product_additional_pickup_instructions`, { errors })}
            value={order.product_additional_pickup_instructions}
            onChange={this.handleOrderPropChange.bind(this, "product_additional_pickup_instructions")}
            label={I18n.humanAttributeName("product_variant", "additional_pickup_instructions")}
            placeholder={I18n.humanAttributeName("product_variant", "additional_pickup_instructions_placeholder")}
            autoHeight
          />

          <button type="submit" disabled={loading} hidden />
        </Form>
      </NewOrderModalView.Content>
    );
  }

  handleUseCustomProductChange(nextUseCustomProduct) {
    this.setState({ useCustomProduct: nextUseCustomProduct });
    if (nextUseCustomProduct) {
      const { order, onOrderUpdate, currentCompany } = this.props;
      onOrderUpdate(
        OrderFullDecorator.build({
          company: currentCompany,
          booking_type: "static_date_booking",
          stock_type: "item",
        })
      );
    }
  }

  render() {
    const { open, onClose, filters, setFilters, onSave, loading } = this.props;
    const { useCustomProduct } = this.state;
    return (
      <NewOrderModalView open onClose={onClose} loading={loading}>
        <NewOrderModalView.Container>
          <NewOrderModalView.Header className="u-flexRow">
            <span>
              {useCustomProduct
                ? I18n.t("papp.orders.NewOrderProductModal.with_custom_product_title")
                : filters.productId
                ? I18n.t("papp.orders.NewOrderProductModal.choose_product_variant")
                : I18n.t("papp.orders.NewOrderProductModal.default_title")}
            </span>
            {!useCustomProduct && (
              <FormControl.Input
                value={filters.query || ""}
                onChange={(query) => setFilters({ query })}
                placeholder={I18n.t("papp.orders.NewOrderProductModal.search_products")}
                className="c-inputStandard u-marginLeft u-fontSizeRelative"
              />
            )}
            {!useCustomProduct && st.authorize("create_as_supplier", { type: "Order" }) && (
              <Button
                as="a"
                primary
                className="u-marginLeftAuto"
                content={I18n.t("papp.orders.NewOrderProductModal.use_custom_product_button")}
                onClick={this.handleUseCustomProductChange.bind(this, true)}
              />
            )}
          </NewOrderModalView.Header>
          {useCustomProduct ? this.renderCustomProductFormContent() : this.renderProductsListContent()}
          {useCustomProduct && (
            <NewOrderModalView.Actions
              goBack={this.handleUseCustomProductChange.bind(this, false)}
              submit={onSave}
              submitDisabled={loading}
              submitContent={I18n.t("papp.common.go_next")}
            />
          )}
        </NewOrderModalView.Container>
      </NewOrderModalView>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/orders/NewOrderModal/NewOrderProductModal/NewOrderProductModal.jsx